import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || '/api/v1';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';

const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
  timeout: 500000,
});

// Response interceptor - handle 401
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      const url = error.config?.url || '';
      if (!url.includes('/auth/me') && !url.includes('/auth/login')) {
        try {
          localStorage.removeItem('user');
        } catch (e) {}
        window.location.replace(`${BACKEND_URL}/api/auth/login`);
      }
    }
    return Promise.reject(error);
  }
);

// =============================================
// API METHODS
// =============================================

// Auth
export const authAPI = {
  getMe: () => api.get('/auth/me'),
  logout: () => api.post('/auth/logout'),
};

// Users
export const userAPI = {
  list: (params) => api.get('/users', { params }),
  getById: (id) => api.get(`/users/${id}`),
  invite: (data) => api.post('/users/invite', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  updateProfile: (data) => api.put('/users/profile', data),
  changePassword: (data) => api.put('/users/change-password', data),
  deactivate: (id) => api.put(`/users/${id}/deactivate`),
  activate: (id) => api.put(`/users/${id}/activate`),
  resendInvite: (id) => api.post(`/users/${id}/resend-invite`),
};

// Projects
export const projectAPI = {
  list: (params) => api.get('/projects', { params }),
  getById: (id) => api.get(`/projects/${id}`),
  create: (data) => api.post('/projects', data),
  update: (id, data) => api.put(`/projects/${id}`, data),
  archive: (id) => api.put(`/projects/${id}/archive`),
  restore: (id) => api.put(`/projects/${id}/restore`),
  addMembers: (id, data) => api.post(`/projects/${id}/members`, data),
  removeMember: (id, userId) => api.delete(`/projects/${id}/members/${userId}`),
};

// Milestones
export const milestoneAPI = {
  list: (params) => api.get('/milestones', { params }),
  create: (data) => api.post('/milestones', data),
  update: (id, data) => api.put(`/milestones/${id}`, data),
  delete: (id) => api.delete(`/milestones/${id}`),
};

// Tasks
export const taskAPI = {
  list: (params) => api.get('/tasks', { params }),
  myTasks: (params) => api.get('/tasks/my', { params }),
  teamTasks: (params) => api.get('/tasks/team', { params }),
  getById: (id) => api.get(`/tasks/${id}`),
  create: (data) => api.post('/tasks', data),
  update: (id, data) => api.put(`/tasks/${id}`, data),
  delete: (id) => api.delete(`/tasks/${id}`),
  addDependency: (id, data) => api.post(`/tasks/${id}/dependencies`, data),
  removeDependency: (id, depId) => api.delete(`/tasks/${id}/dependencies/${depId}`),
  flagOverdue: () => api.post('/tasks/flag-overdue'),
};

// Comments
export const commentAPI = {
  list: (params) => api.get('/comments', { params }),
  create: (data) => api.post('/comments', data),
  update: (id, data) => api.put(`/comments/${id}`, data),
  delete: (id) => api.delete(`/comments/${id}`),
};

// Attachments
export const attachmentAPI = {
  list: (params) => api.get('/attachments', { params }),
  upload: (formData) => api.post('/attachments', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  delete: (id) => api.delete(`/attachments/${id}`),
};

// Notifications
export const notificationAPI = {
  list: (params) => api.get('/notifications', { params }),
  unreadCount: () => api.get('/notifications/unread-count'),
  markRead: (id) => api.put(`/notifications/${id}/read`),
  markAllRead: () => api.put('/notifications/read-all'),
};

// Dashboard & Reports
export const dashboardAPI = {
  get: () => api.get('/dashboard'),
};

export const reportAPI = {
  overdue: (params) => api.get('/reports/overdue', { params }),
  workload: (params) => api.get('/reports/workload', { params }),
  progress: (params) => api.get('/reports/progress', { params }),
  completion: (params) => api.get('/reports/completion', { params }),
  activity: (params) => api.get('/reports/activity', { params }),
};

// AI (Gemini-Powered)
export const aiAPI = {
  status: () => api.get('/ai/status'),
  ask: (data) => api.post('/ai/ask', data),
  dailyDigest: () => api.post('/ai/daily-digest'),
  generateTaskDescription: (data) => api.post('/ai/task-description', data),
  summarizeComments: (data) => api.post('/ai/comments/summarize', data),
  projectSummary: (projectId) => api.post(`/ai/project/${projectId}/summary`),
  riskAnalysis: (projectId) => api.post(`/ai/project/${projectId}/risks`),
  statusReport: (projectId) => api.post(`/ai/project/${projectId}/status-report`),
  suggestTasks: (projectId) => api.post(`/ai/project/${projectId}/suggest-tasks`),
  optimizeWorkload: (projectId) => api.post(`/ai/project/${projectId}/optimize-workload`),
  meetingNotes: (projectId, data) => api.post(`/ai/project/${projectId}/meeting-notes`, data),
  timelinePrediction: (projectId) => api.post(`/ai/project/${projectId}/timeline`),
};

// RFP Management
export const rfpAPI = {
  list: (params) => api.get('/rfps', { params }),
  getById: (id) => api.get(`/rfps/${id}`),
  create: (data) => api.post('/rfps', data),
  update: (id, data) => api.put(`/rfps/${id}`, data),
  delete: (id) => api.delete(`/rfps/${id}`),
  generateWithAI: (id, data) => api.post(`/rfps/${id}/generate`, data),
  updateSection: (rfpId, sectionId, data) => api.put(`/rfps/${rfpId}/sections/${sectionId}`, data),
  regenerateSection: (rfpId, sectionId, data) => api.post(`/rfps/${rfpId}/sections/${sectionId}/regenerate`, data),
  shareWithVendor: (rfpId, data) => api.post(`/rfps/${rfpId}/share`, data),
  listVendors: (rfpId) => api.get(`/rfps/${rfpId}/vendors`),
  removeVendor: (rfpId, vendorId) => api.delete(`/rfps/${rfpId}/vendors/${vendorId}`),
};

// Proposal Evaluation
export const proposalAPI = {
  list: (params) => api.get('/proposals', { params }),
  getById: (id) => api.get(`/proposals/${id}`),
  create: (formData) => api.post('/proposals', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  evaluate: (id) => api.post(`/proposals/${id}/evaluate`),
  addComment: (proposalId, evalId, data) => api.put(`/proposals/${proposalId}/evaluate/${evalId}`, data),
  updateStatus: (id, data) => api.put(`/proposals/${id}/status`, data),
  sendFeedback: (id, data) => api.post(`/proposals/${id}/feedback`, data),
  compare: (params) => api.post('/proposals/compare', null, { params }),
  dashboard: () => api.get('/rfp-dashboard'),
};

export default api;
